import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
} from "@mui/material";
import { Close, InfoOutlined } from "@mui/icons-material";
import { useSelectedOrganization, useTranslations, useSnackbar } from "hooks";
import { organizationsApi, processmodelsApi, baseApi } from "api";
import { getTranslation } from "common";
import { RobotFormRender, RobotValuesEditorRef } from "components/RobotFormRender";
import { Robot } from "hooks/useRobots";
import { Data } from "./ProcessModelSchema";
import { ProcessModelDataTypes } from "hooks/useBpmnModeler/useBpmnJsModeler";

interface RobotPropertiesDialogProps {
    open: boolean;
    onClose: () => void;
    robot?: Robot | { id?: number, name?: string };
    onUpdate?: () => Promise<void>;
    sectionName?: string;
    processModelId?: any;
    element?: any;
    userdataschema?: Data[];
    processModelData?: ProcessModelDataTypes;
    onNext?: () => void;
    onPrev?: () => void;
    totalSections?: number;
    currentSectionIndex?: number;
    robotValidationRef?: React.MutableRefObject<(() => boolean) | null>;
}

const RobotPropertiesDialog: React.FC<RobotPropertiesDialogProps> = ({
    open,
    onClose,
    robot,
    onUpdate,
    processModelId,
    element,
    userdataschema = [],
    sectionName,
    currentSectionIndex,
    totalSections,
    onNext,
    onPrev,
}) => {
    const translate = useTranslations();
    const organization = useSelectedOrganization();
    const { sendSnack } = useSnackbar();
    const translations = useTranslations();
    const [fullSchema, setFullSchema] = useState<any>(null);
    const [localSchema, setLocalSchema] = useState<any>(null);
    const [displaySchema, setDisplaySchema] = useState<any>(null);
    const [initialValues, setInitialValues] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [userDataAnchorEl, setUserDataAnchorEl] = useState<null | HTMLElement>(null);
    const [currentFieldName, setCurrentFieldName] = useState<string | null>(null);
    const [currentSubFieldName, setCurrentSubFieldName] = useState<'min' | 'max' | null>(null); //deprecated
    const [internalProcessModelData, setInternalProcessModelData] = useState<any>(null);

    const formRef = useRef<RobotValuesEditorRef>(null);

    useEffect(() => {
        if (!open) {
            setFullSchema(null);
            setLocalSchema(null);
            setDisplaySchema(null);
            setInitialValues({});
            return;
        }
        let isMounted = true;

        async function loadData() {
            if (!robot?.id || !organization?.id) return;

            setLoading(true);
            try {
                const settingsPromise = baseApi.get(`/organizations/${organization.id}/robots/${robot.id}/settings`);

                if (processModelId) {
                    const processModelPromise = processmodelsApi.get(`/${processModelId}`);
                    const [settingsRes, processModelRes] = await Promise.all([settingsPromise, processModelPromise]);

                    if (!isMounted) return;

                    setInternalProcessModelData(processModelRes.data);

                    const extensionRobots = processModelRes.data.extensionrobots;
                    const robotFromExtension = extensionRobots?.find(
                        (r: any) => r.idrobot === robot.id && r.linkedto === element.id
                    );

                    const finalSchema = robotFromExtension?.datajson || settingsRes.data.datajson;
                    setFullSchema(finalSchema);
                } else {
                    const { data } = await settingsPromise;
                    if (!isMounted) return;
                    if (data?.datajson) {
                        setFullSchema(data.datajson);
                    } else {
                        setFullSchema(null);
                    }
                }
            } catch (err) {
                if (isMounted) {
                    setFullSchema(null);
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        }

        loadData();

        return () => {
            isMounted = false;
        };
    }, [robot?.id, open, organization, processModelId, element?.id]);

    useEffect(() => {
        if (fullSchema) {
            setLocalSchema(JSON.parse(JSON.stringify(fullSchema)));
        } else {
            setLocalSchema(null);
        }
    }, [fullSchema]);

    useEffect(() => {
        if (!localSchema) {
            setDisplaySchema(null);
            setInitialValues({});
            return;
        }

        let currentDisplaySchema: any = null;
        if (sectionName && localSchema[sectionName]) {
            currentDisplaySchema = { [sectionName]: localSchema[sectionName] };
        } else if (!sectionName && totalSections === undefined) {
            currentDisplaySchema = localSchema;
        }

        const loadedValues: any = {};
        if (currentDisplaySchema) {
            Object.values(currentDisplaySchema).forEach((section: any) => {
                if (section?.properties) {
                    Object.entries(section.properties).forEach(([key, prop]: [string, any]) => {
                        loadedValues[key] = prop.value;
                    });
                }
            });
        }

        setDisplaySchema(currentDisplaySchema);
        setInitialValues(loadedValues);
    }, [localSchema, sectionName, totalSections]);

    const handleSave = async (schemaToSave: any) => {
        if (!robot?.id || !organization?.id || !schemaToSave) return;

        setLoading(true);

        try {
            if (processModelId) {
                if (!processModelId || !element || !internalProcessModelData) return;

                const currentProcessModel = internalProcessModelData;
                const existingRobots = currentProcessModel.extensionrobots || [];

                const newExtensionRobot = {
                    linkedto: element.id,
                    idrobot: robot.id,
                    datajson: schemaToSave,
                };

                const robotIndex = existingRobots.findIndex(
                    (r: any) => r.idrobot === robot.id && r.linkedto === element.id
                );

                let updatedRobots;
                if (robotIndex > -1) {
                    updatedRobots = [...existingRobots];
                    updatedRobots[robotIndex] = newExtensionRobot;
                } else {
                    updatedRobots = [...existingRobots, newExtensionRobot];
                }

                const payload = {
                    ...currentProcessModel,
                    extensionrobots: updatedRobots,
                };

                await processmodelsApi.put(`/${processModelId}`, payload);

            } else {
                const resp = await organizationsApi.put(`/${organization.id}/robots/${robot.id}/settings`, {
                    datajson: schemaToSave
                });
                sendSnack({ message: resp.data.responsemessage, type: "success" });
            }

            if (onUpdate) await onUpdate();
            onClose();

        } catch {
        }
        finally {
            setLoading(false);
        }
    };

    const handleNext = async () => {
        if (!formRef.current) return;

        if (!formRef.current.isValid()) {
            sendSnack({ message: getTranslation(translate, "register.fieldrequired"), type: "error" });
            return;
        }

        const currentFormData = formRef.current.getFormData();
        const updatedLocalSchema = JSON.parse(JSON.stringify(localSchema));

        if (sectionName && updatedLocalSchema && updatedLocalSchema[sectionName]) {
            updatedLocalSchema[sectionName] = currentFormData[sectionName];
        } else {
            Object.keys(currentFormData).forEach(key => {
                if (updatedLocalSchema && updatedLocalSchema[key]) {
                    updatedLocalSchema[key] = currentFormData[key];
                }
            });
        }
        setLocalSchema(updatedLocalSchema);
        onNext && onNext();
    };

    const handlePrev = async () => {
        if (!formRef.current) return;

        const currentFormData = formRef.current.getFormData();
        const updatedLocalSchema = JSON.parse(JSON.stringify(localSchema));

        if (sectionName && updatedLocalSchema && updatedLocalSchema[sectionName]) {
            updatedLocalSchema[sectionName] = currentFormData[sectionName];
        } else {
            Object.keys(currentFormData).forEach(key => {
                if (updatedLocalSchema && updatedLocalSchema[key]) {
                    updatedLocalSchema[key] = currentFormData[key];
                }
            });
        }
        setLocalSchema(updatedLocalSchema);
        onPrev && onPrev();
    };

    const handleSaveClick = async () => {
        if (!formRef.current) return;

        if (!formRef.current.isValid()) {
            sendSnack({ message: getTranslation(translate, "register.fieldrequired"), type: "error" });
            return;
        }

        const currentFormData = formRef.current.getFormData();
        const updatedLocalSchema = JSON.parse(JSON.stringify(localSchema));

        if (sectionName && updatedLocalSchema && updatedLocalSchema[sectionName]) {
            updatedLocalSchema[sectionName] = currentFormData[sectionName];
        } else {
            Object.keys(currentFormData).forEach(key => {
                if (updatedLocalSchema && updatedLocalSchema[key]) {
                    updatedLocalSchema[key] = currentFormData[key];
                }
            });
        }
        await handleSave(updatedLocalSchema);
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const handleMenuClose = () => {
        setUserDataAnchorEl(null);
        setCurrentFieldName(null);
        setCurrentSubFieldName(null);
    };

    const handleUserDataSelect = (userData: Data) => {
        if (currentFieldName && formRef.current) {

            const valueToInsert = `{{${userData.name}}}`;
            const currentFormData = formRef.current.getFormData();
            const updatedSchema = JSON.parse(JSON.stringify(currentFormData));

            for (const sectionKey in updatedSchema) {
                const section = updatedSchema[sectionKey];
                if (section?.properties && section.properties[currentFieldName]) {
                    if (currentSubFieldName) {
                        if (!section.properties[currentFieldName].value) {
                            section.properties[currentFieldName].value = { min: "", max: "" };
                        }
                        section.properties[currentFieldName].value[currentSubFieldName] = valueToInsert;
                    } else {
                        section.properties[currentFieldName].value = valueToInsert;
                    }
                    break;
                }
            }

            const values: any = {};
            Object.values(updatedSchema).forEach((section: any) => {
                if (section?.properties) {
                    Object.entries(section.properties).forEach(([key, def]: any) => {
                        values[key] = def.value;
                    });
                }
            });

            formRef.current.setFormData(values);
        }
        handleMenuClose();
    };

    const handleUserDataTrigger = (
        fieldName: string,
        anchorEl: HTMLElement,
        subFieldName?: 'min' | 'max'
    ) => {
        setCurrentFieldName(fieldName);
        setUserDataAnchorEl(anchorEl);
        setCurrentSubFieldName(subFieldName || null);
    };

    const titleKey = sectionName || (processModelId ? "robot.settings" : "robot.edit.values");
    const isWizard = totalSections !== undefined && totalSections > 0;
    const isLastSection = isWizard && currentSectionIndex !== undefined ? currentSectionIndex === totalSections - 1 : true;
    const isFirstSection = isWizard && currentSectionIndex !== undefined ? currentSectionIndex === 0 : false;

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">{titleKey}</Typography>
                        <Box display="flex" alignItems="center">
                            {processModelId && (
                                <Tooltip title={getTranslation(translations, "robot.userschema_variables_info")}>
                                    <InfoOutlined sx={{ ml: 2 }} />
                                </Tooltip>
                            )}
                            <IconButton onClick={handleClose} disabled={loading}>
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={4}><Typography>Caricamento...</Typography></Box>
                    ) : displaySchema ? (
                        <RobotFormRender
                            key={sectionName || 'all-sections'}
                            ref={formRef}
                            schema={displaySchema}
                            initialData={initialValues}
                            showSaveButton={false}
                            onUserDataTrigger={handleUserDataTrigger}
                            userdataschema={userdataschema}
                        />

                    ) : (
                        <Typography>{getTranslation(translations, "robot.no.schema")}</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>
                        {getTranslation(translations, "generic.button.cancel")}
                    </Button>
                    {isWizard ? (
                        <>
                            {!isFirstSection && (
                                <Button onClick={handlePrev} disabled={loading}>
                                    {getTranslation(translations, "generic.previous")}
                                </Button>
                            )}
                            {!isLastSection && (
                                <Button onClick={handleNext} variant="contained" disabled={loading}>
                                    {getTranslation(translations, "process.action.next")}
                                </Button>
                            )}
                            {isLastSection && (
                                <Button onClick={handleSaveClick} variant="contained" disabled={loading}>
                                    {getTranslation(translations, "pm.button.save")}
                                </Button>
                            )}

                        </>
                    ) : (
                        <Button onClick={handleSaveClick} variant="contained" disabled={loading}>
                            {getTranslation(translations, "pm.button.save")}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {processModelId && (
                <Menu anchorEl={userDataAnchorEl} open={Boolean(userDataAnchorEl)} onClose={handleMenuClose}>
                    {userdataschema.length > 0 ? (
                        userdataschema.map((data) => (
                            <MenuItem key={data.id} onClick={() => handleUserDataSelect(data)}>
                                {data.name}
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled>No user data available</MenuItem>
                    )}
                </Menu>
            )}
        </>
    );
};

export default RobotPropertiesDialog;